/**
 * ElementPropertiesPanel
 *
 * Side panel that shows editable properties for the currently selected
 * label element. Renders different fields depending on element type.
 */
import { ArrowDropDown, ArrowDropUp, Delete } from '@mui/icons-material';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { LabelTemplateElement } from '../../types/printing';
import { TEMPLATE_VARIABLES } from '../../types/printing';

interface ElementPropertiesPanelProps {
  element: LabelTemplateElement;
  index: number;
  onUpdate: (index: number, field: string, value: unknown) => void;
  onRemove: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  isFirst: boolean;
  isLast: boolean;
}

/** Reusable field that toggles between dots (integer) and percentage via an end adornment. */
function DotPercentField({
  label,
  value,
  onChange,
  sx,
}: {
  label: string;
  value: unknown;
  onChange: (value: string | number) => void;
  sx?: object;
}) {
  const strVal = String(value ?? '0');
  const isPercent = strVal.endsWith('%');
  const numericPart = isPercent ? strVal.slice(0, -1) : strVal;

  const toggle = () => {
    const num = parseInt(numericPart) || 0;
    // Clamp to 0-100 when switching to percent so ZPL builder doesn't reject it
    onChange(isPercent ? num : `${Math.max(0, Math.min(100, num))}%`);
  };

  const handleChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9-]/g, '');
    if (isPercent) {
      onChange(`${cleaned || 0}%`);
    } else {
      onChange(parseInt(cleaned) || 0);
    }
  };

  return (
    <TextField
      label={label}
      value={numericPart}
      onChange={e => handleChange(e.target.value)}
      size="small"
      sx={sx}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={isPercent ? 'Switch to dots' : 'Switch to percent'}>
                <IconButton
                  size="small"
                  onClick={toggle}
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    px: 0.5,
                    minWidth: 24,
                    borderRadius: 1,
                  }}
                >
                  {isPercent ? '%' : '#'}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

const TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  fieldblock: 'Text Block',
  qrcode: 'QR Code',
  barcode: 'Barcode',
  box: 'Box / Border',
  line: 'Line',
  'inverse-box': 'Inverse Box',
};

export function ElementPropertiesPanel({
  element,
  index,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: ElementPropertiesPanelProps) {
  const opts = (element.options || {}) as Record<string, unknown>;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={TYPE_LABELS[element.type] || element.type}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            #{index + 1}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Move up">
            <span>
              <IconButton size="small" disabled={isFirst} onClick={() => onMoveUp(index)}>
                <ArrowDropUp fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton size="small" disabled={isLast} onClick={() => onMoveDown(index)}>
                <ArrowDropDown fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete element">
            <IconButton size="small" color="error" onClick={() => onRemove(index)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      <Stack spacing={1.5}>
        {/* Position */}
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          Position
        </Typography>
        <Stack direction="row" spacing={1}>
          <DotPercentField
            label="X"
            value={opts.x ?? 0}
            onChange={v => onUpdate(index, 'x', v)}
            sx={{ flex: 1 }}
          />
          <DotPercentField
            label="Y"
            value={opts.y ?? 0}
            onChange={v => onUpdate(index, 'y', v)}
            sx={{ flex: 1 }}
          />
        </Stack>

        {/* Content */}
        {element.content !== undefined && (
          <>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Content
            </Typography>
            <TextField
              value={element.content}
              onChange={e => onUpdate(index, 'content', e.target.value)}
              size="small"
              fullWidth
              multiline={element.type === 'fieldblock'}
              rows={element.type === 'fieldblock' ? 2 : 1}
            />
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {Object.entries(TEMPLATE_VARIABLES).map(([key, desc]) => (
                <Tooltip key={key} title={desc}>
                  <Chip
                    label={key}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const current = String(element.content || '');
                      onUpdate(index, 'content', current + key);
                    }}
                    sx={{ fontSize: '0.65rem', height: 22, cursor: 'pointer' }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </>
        )}

        <Divider />

        {/* Type-specific options */}
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          Style
        </Typography>

        {/* Font size for text/fieldblock */}
        {(element.type === 'text' || element.type === 'fieldblock') && (
          <TextField
            label="Font Size (dots)"
            type="number"
            value={opts.fontSize ?? 20}
            onChange={e => onUpdate(index, 'fontSize', parseInt(e.target.value) || 20)}
            size="small"
          />
        )}

        {/* Text alignment */}
        {element.type === 'fieldblock' && (
          <Select
            value={String(opts.alignment ?? 'L')}
            onChange={e => onUpdate(index, 'alignment', e.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value="L">Left</MenuItem>
            <MenuItem value="C">Center</MenuItem>
            <MenuItem value="R">Right</MenuItem>
          </Select>
        )}

        {/* QR magnification */}
        {element.type === 'qrcode' && (
          <>
            <TextField
              label="QR Magnification"
              type="number"
              value={opts.magnificationFactor ?? 3}
              onChange={e => onUpdate(index, 'magnificationFactor', parseInt(e.target.value) || 3)}
              size="small"
              slotProps={{
                htmlInput: { min: 1, max: 10 },
              }}
            />
            <Select
              value={String(opts.errorCorrection ?? 'M')}
              onChange={e => onUpdate(index, 'errorCorrection', e.target.value)}
              size="small"
              fullWidth
            >
              <MenuItem value="L">Low (7%)</MenuItem>
              <MenuItem value="M">Medium (15%)</MenuItem>
              <MenuItem value="Q">Quartile (25%)</MenuItem>
              <MenuItem value="H">High (30%)</MenuItem>
            </Select>
          </>
        )}

        {/* Width for fieldblock, box, line, inverse-box */}
        {(element.type === 'fieldblock' ||
          element.type === 'box' ||
          element.type === 'line' ||
          element.type === 'inverse-box') && (
          <DotPercentField
            label="Width"
            value={opts.width ?? 100}
            onChange={v => onUpdate(index, 'width', v)}
          />
        )}

        {/* Height for box, line, inverse-box, barcode */}
        {(element.type === 'box' ||
          element.type === 'line' ||
          element.type === 'inverse-box' ||
          element.type === 'barcode') && (
          <DotPercentField
            label="Height"
            value={opts.height ?? 20}
            onChange={v => onUpdate(index, 'height', v)}
          />
        )}

        {/* Border thickness for box, inverse-box */}
        {(element.type === 'box' || element.type === 'inverse-box') && (
          <TextField
            label="Border Thickness"
            type="number"
            value={opts.borderThickness ?? 2}
            onChange={e => onUpdate(index, 'borderThickness', parseInt(e.target.value) || 1)}
            size="small"
          />
        )}

        {/* Line thickness */}
        {element.type === 'line' && (
          <TextField
            label="Thickness"
            type="number"
            value={opts.thickness ?? 2}
            onChange={e => onUpdate(index, 'thickness', parseInt(e.target.value) || 1)}
            size="small"
          />
        )}

        {/* Fieldblock max lines */}
        {element.type === 'fieldblock' && (
          <TextField
            label="Max Lines"
            type="number"
            value={opts.max ?? 1}
            onChange={e => onUpdate(index, 'max', parseInt(e.target.value) || 1)}
            size="small"
          />
        )}

        {/* Barcode human readable */}
        {element.type === 'barcode' && (
          <Select
            value={String(opts.humanReadable ?? 'Y')}
            onChange={e => onUpdate(index, 'humanReadable', e.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value="Y">Show text below barcode</MenuItem>
            <MenuItem value="N">Barcode only</MenuItem>
          </Select>
        )}
      </Stack>
    </Box>
  );
}
